/**
 * Main forecast projection — funnel × cohorts × channels × events → MRR bridge + cash.
 */

import { ageCohorts, normalizeRetention, scaleRetentionChurn } from './cohorts';
import { computeChannelSignups, applyTamCap, PAID_BUDGET_MODE_CUSTOM } from './channels';
import { resolveMarketingBudgetMo } from './marketingPhases';
import { applyEvents } from './events';
import {
  processFunnelMonth,
  weightedArpu,
  newMrrFromLogos,
  cumulativeSignupToPaid,
  bankLinkedLogosFromPaid,
  normalizeFunnel,
  splitLogosByPlan,
  normalizePlanMix,
  normalizePlanMixGrowth,
  evolvePlanMix,
  upgradeExpansionFromMix,
} from './funnel';
import {
  rollForwardMrr,
  computeExpansionContraction,
  buildMrrBridgeRow,
} from './mrrRollForward';
import { resolveOrganicGrowthRate, packWithGrowthRegime, sampleGrowthRegime } from './growthOptions';

function roundMoney(n) {
  return Math.round(n * 100) / 100;
}

function nonNegativeMultiplier(value, fallback = 1) {
  const n = Number(value);
  return Number.isFinite(n) ? Math.max(0, n) : fallback;
}

/**
 * Single forecast path (deterministic or one MC draw).
 * @param {object} pack — assumptions, funnel, cohorts, channels, events, growth
 * @param {object} [options]
 * @param {(() => number)|null} [options.rng] — when set, sample phased growth + use with MC
 * @param {object} [options.sampleOverrides] — sampled conversion/churn/CAC multipliers
 */
export function projectForecastPath(pack, options = {}) {
  const { rng = null, sampleOverrides = {} } = options;
  const { assumptions, funnel, cohorts, channels, events, growth } = pack;
  const retention = normalizeRetention(cohorts?.retention);
  const normalizedFunnel = normalizeFunnel(funnel);
  const horizon = Math.min(
    60,
    Math.max(1, Math.floor(Number(assumptions.horizonMonths) || 24))
  );
  const openingMix = normalizePlanMix(assumptions.planMix);
  const planMixGrowth = normalizePlanMixGrowth(assumptions.planMixGrowth);
  const openingMixArpu = weightedArpu(assumptions.arpuByTier, openingMix);

  // Shock cumulative conversion once — not each funnel stage (that cubed the vol).
  const convMultiplier = nonNegativeMultiplier(sampleOverrides.conversionMultiplier);
  const churnMultiplier = nonNegativeMultiplier(sampleOverrides.churnMultiplier);
  const cacMultiplier = nonNegativeMultiplier(sampleOverrides.cacMultiplier);

  const adjustedFunnel = normalizedFunnel;

  // Paid CAC can be MC-shocked; monthly paid $ resolved inside the loop (link vs custom;
  // optional assumptions.marketingPhases). Organic growth never reads marketing spend.
  const adjustedChannels = channels?.enabled
    ? {
        ...channels,
        paid: channels.paid
          ? {
              ...channels.paid,
              cac: channels.paid.enabled
                ? Math.max(1, (channels.paid.cac || 1) * cacMultiplier)
                : channels.paid.cac,
            }
          : channels.paid,
      }
    : channels;

  let mrr = Math.max(0, Number(assumptions.openingMrr) || 0);
  let logos = Math.max(0, Number(assumptions.openingLogos) || 0);
  const cashStart = Math.max(0, Number(assumptions.startingCash) || 0);
  let cash = cashStart;

  // Book ARPU from opening MRR/logos so first-month churn $ matches the ledger.
  // New logos price at that month's evolved plan-mix ARPU.
  const bookArpu =
    logos > 0 && mrr > 0 ? roundMoney(mrr / logos) : openingMixArpu;
  let mix = openingMix;

  const cohortList = [
    {
      vintageMonth: 0,
      logos,
      arpu: bookArpu,
    },
  ];

  const signupPipeline = new Map();
  const logosHistory = [logos];
  let priorOrganic =
    adjustedChannels?.organic?.monthlySignups ??
    normalizedFunnel.baseMonthlySignups ??
    0;
  let priorFunnelSignups = normalizedFunnel.baseMonthlySignups;
  const months = [];

  for (let m = 0; m < horizon; m += 1) {
    const prevMrr = mrr;
    const prevLogos = logos;

    const eventEffects = applyEvents(events, m);

    const organicGrowthRate = resolveOrganicGrowthRate(growth, m, rng);
    const marketingSpend = resolveMarketingBudgetMo(assumptions, m);
    const paidAcquisitionBudget =
      adjustedChannels?.paid?.budgetMode === PAID_BUDGET_MODE_CUSTOM
        ? Math.max(0, adjustedChannels.paid.budgetMo || 0)
        : marketingSpend;

    let signups = normalizedFunnel.baseMonthlySignups;
    if (m > 0) {
      if (organicGrowthRate != null) {
        priorFunnelSignups *= 1 + organicGrowthRate;
        signups = priorFunnelSignups;
      } else {
        signups = signups * (1 + normalizedFunnel.signupGrowthPctMo / 100);
        priorFunnelSignups = signups;
      }
    } else {
      priorFunnelSignups = signups;
    }

    if (adjustedChannels?.enabled) {
      const referralLag = Math.max(0, adjustedChannels.referral?.lagMonths ?? 1);
      const referralSourceLogos =
        referralLag === 0
          ? prevLogos
          : m - referralLag >= 0
            ? logosHistory[m - referralLag] ?? 0
            : 0;
      const channelSignups = computeChannelSignups(
        adjustedChannels,
        m,
        priorOrganic,
        referralSourceLogos,
        [],
        { organicGrowthRate, paidBudgetMo: paidAcquisitionBudget }
      );
      signups = channelSignups.total;
      priorOrganic = channelSignups.organicOut;
    }

    signups = Math.max(0, signups + eventEffects.deltaSignups);

    const funnelResult = processFunnelMonth(
      signups,
      adjustedFunnel,
      m,
      signupPipeline,
      eventEffects.conversionDeltaPp
    );

    const sampledNewLogos = funnelResult.newPaidLogos * convMultiplier;
    // A conversion multiplier is uncertainty around the configured rate, not
    // permission to convert more than every matured signup.
    let newLogos = Math.max(
      0,
      Math.min(funnelResult.maturedSignups, sampledNewLogos)
    );

    let churnedLogos = 0;
    let churnedMrr = 0;

    if (m > 0) {
      const adjustedRetention = scaleRetentionChurn(retention, churnMultiplier);
      const aged = ageCohorts(
        cohortList,
        m,
        adjustedRetention,
        eventEffects.churnDeltaPp
      );
      cohortList.length = 0;
      cohortList.push(...aged.cohorts);
      churnedLogos = aged.churnedLogos;
      churnedMrr = aged.churnedMrr;
    }

    // TAM is an ending active-logo cap. Churn frees capacity in the same
    // month, so apply it after aging rather than against pre-churn logos.
    const postChurnLogos = cohortList.reduce((sum, cohort) => sum + cohort.logos, 0);
    newLogos = applyTamCap(postChurnLogos, newLogos, adjustedChannels?.tamCap);

    const priorMix = mix;
    if (m > 0) {
      mix = evolvePlanMix(mix, planMixGrowth);
    }
    const mixArpu = weightedArpu(assumptions.arpuByTier, mix);
    const newMrr = newMrrFromLogos(newLogos, mixArpu);

    if (newLogos > 0) {
      cohortList.push({ vintageMonth: m, logos: newLogos, arpu: mixArpu });
    }

    logos = cohortList.reduce((s, c) => s + c.logos, 0);
    logosHistory[m] = logos;
    const logosByPlan = splitLogosByPlan(logos, mix);
    const bankLinkedLogos = bankLinkedLogosFromPaid(logos, adjustedFunnel);
    const newBankLinkedLogos = bankLinkedLogosFromPaid(newLogos, adjustedFunnel);

    const retainedMrr = m === 0 ? prevMrr : Math.max(0, prevMrr - churnedMrr);
    const upgradeExpansion =
      m === 0
        ? 0
        : upgradeExpansionFromMix(
            postChurnLogos,
            priorMix,
            mix,
            assumptions.arpuByTier
          );
    const { expansion: pctExpansion, contraction } =
      m === 0
        ? { expansion: 0, contraction: 0 }
        : computeExpansionContraction(
            retainedMrr,
            assumptions.expansionPctMo,
            assumptions.contractionPctMo
          );
    const expansion = roundMoney(pctExpansion + upgradeExpansion);

    mrr = Math.max(
      0,
      rollForwardMrr(prevMrr, {
        newMrr,
        expansion,
        contraction,
        churnMrr: churnedMrr,
        shock: eventEffects.mrrShock,
      })
    );

    // Plaid is incurred by linked logos; Stripe is incurred by every paid logo.
    // Keep the legacy blended scalar for pre-v7 packs that have no split rates.
    // AI pack revenue is non-MRR (overage packs); still hits cash + monthly profit.
    const hasSplitCogs =
      assumptions.plaidCogsPerLinkedLogo != null ||
      assumptions.stripeCogsPerPaidLogo != null;
    const plaidCogs =
      hasSplitCogs
        ? bankLinkedLogos * Math.max(0, Number(assumptions.plaidCogsPerLinkedLogo) || 0)
        : 0;
    const stripeCogs =
      hasSplitCogs
        ? logos * Math.max(0, Number(assumptions.stripeCogsPerPaidLogo) || 0)
        : 0;
    const variableCogs = hasSplitCogs
      ? plaidCogs + stripeCogs
      : logos * Math.max(0, Number(assumptions.variableCogsPerLogo) || 0);
    const aiCogs = Math.max(0, Number(assumptions.aiCogsPerLogo) || 0);
    const aiPackRevenue = roundMoney(
      logos * Math.max(0, Number(assumptions.aiPackRevenuePerLogo) || 0)
    );
    const opex =
      Math.max(0, Number(assumptions.fixedOpexMo) || 0) +
      variableCogs +
      logos * aiCogs +
      marketingSpend;
    const burn = opex - mrr - aiPackRevenue;
    const monthlyProfit = roundMoney(mrr + aiPackRevenue - opex);
    cash = roundMoney(cash - burn + (eventEffects.cashInjection || 0));

    const bridge = buildMrrBridgeRow({
      monthIndex: m,
      prevMrr,
      newMrr,
      expansion,
      contraction,
      churnMrr: churnedMrr,
      shock: eventEffects.mrrShock,
      endMrr: mrr,
      prevLogos,
      newLogos,
      churnedLogos,
      endLogos: logos,
    });

    months.push({
      ...bridge,
      arpu: mixArpu,
      bookArpu,
      cash,
      burn: roundMoney(burn),
      monthlyProfit,
      marketingSpend: roundMoney(marketingSpend),
      variableCogsMo: roundMoney(variableCogs),
      plaidCogsMo: roundMoney(plaidCogs),
      stripeCogsMo: roundMoney(stripeCogs),
      aiCogsMo: roundMoney(logos * aiCogs),
      aiPackRevenue,
      signups: roundPct(signups),
      convRate:
        funnelResult.maturedSignups > 0
          ? newLogos / funnelResult.maturedSignups
          : funnelResult.convRate,
      logosByPlan,
      planMix: mix,
      bankLinkedLogos: roundPct(bankLinkedLogos),
      newBankLinkedLogos: roundPct(newBankLinkedLogos),
      bankLinkRate: adjustedFunnel.paidToBankLinkPct / 100,
      eventLabels: eventEffects.labels,
    });
  }

  const runwayMonths = computeRunway(months);
  const monthsToProfit = computeMonthsToProfit(months);

  return {
    months,
    arpu: openingMixArpu,
    convRate: cumulativeSignupToPaid(adjustedFunnel),
    bankLinkRate: adjustedFunnel.paidToBankLinkPct / 100,
    runwayMonths,
    monthsToProfit,
    openingCash: cashStart,
    mc: false,
  };
}

function roundPct(n) {
  return Math.round(n * 10) / 10;
}

/**
 * First month index where monthlyProfit > 0, or null if never within horizon.
 * @param {object[]} months
 * @returns {number|null}
 */
export function computeMonthsToProfit(months) {
  for (let i = 0; i < months.length; i += 1) {
    if (months[i].monthlyProfit > 0) return i;
  }
  return null;
}

/**
 * Months-to-profit for the active Results band (matches bridge profitP10/P50/P90).
 * Prefer this over run-level MTP percentiles so KPI tracks the selected page band.
 * @param {object[]} months
 * @param {'p10'|'p50'|'p90'} [band]
 * @returns {number|null}
 */
export function computeMonthsToProfitFromBand(months, band = 'p50') {
  const key =
    band === 'p10' ? 'profitP10' : band === 'p90' ? 'profitP90' : 'profitP50';
  for (let i = 0; i < (months || []).length; i += 1) {
    const profit =
      months[i][key] ?? months[i].monthlyProfit ?? months[i].profit ?? null;
    if (profit != null && profit > 0) return i;
  }
  return null;
}

/**
 * Months until cash recovers to opening after a drawdown ("opening-cash recovery").
 * @param {number[]} cashByMonth — band cash path, index 0 = opening
 * @returns {number|null} 0 = never drew down; null = drew down, never recovered
 */
export function computeCashRecoveryMonths(cashByMonth) {
  if (!cashByMonth?.length) return null;
  const opening = cashByMonth[0];
  if (opening == null || !Number.isFinite(opening)) return null;
  const eps = Math.max(0.01, Math.abs(opening) * 1e-9);
  let dipped = false;
  for (let i = 1; i < cashByMonth.length; i += 1) {
    const cash = cashByMonth[i];
    if (cash == null || !Number.isFinite(cash)) continue;
    if (cash < opening - eps) dipped = true;
    if (dipped && cash >= opening - eps) return i;
  }
  return dipped ? null : 0;
}

function cashPathForBand(openingCash, months, band) {
  const key = band === 'p10' ? 'cashP10' : band === 'p90' ? 'cashP90' : 'cashP50';
  return [openingCash, ...(months || []).map((m) => m[key] ?? m.cash)];
}

/**
 * Months until cash <= 0 (from month 1 onward).
 * @param {object[]} months
 */
export function computeRunway(months) {
  if (months?.[0]?.cash <= 0) return 0;
  for (let i = 1; i < months.length; i += 1) {
    if (months[i].cash <= 0) return i;
  }
  return months.length;
}

/**
 * Project active pack or named scenario key.
 * @param {object} scenario
 * @param {string} [packKey]
 */
export function projectForecast(scenario, packKey) {
  const key = packKey || scenario.activeScenario || 'base';
  const pack = scenario.packs?.[key];
  if (!pack) return { months: [], mc: false };

  if (scenario.monteCarlo?.enabled) {
    return projectForecastMonteCarlo(scenario, pack);
  }

  return projectForecastPath(pack);
}

function createRng(seed) {
  let state = seed >>> 0;
  return () => {
    state += 0x6d2b79f5;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function gaussian(rng) {
  let u = Number(rng());
  let v = Number(rng());
  if (!(u > 0)) u = Number.MIN_VALUE;
  if (!(v > 0)) v = Number.MIN_VALUE;
  u = Math.min(1, u);
  v = Math.min(1, v);
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}

function sampleMultiplier(rng, volPct) {
  const sigma = Math.min(1, Math.max(0, Number(volPct) / 100));
  if (!rng || !Number.isFinite(sigma) || sigma <= 0) return 1;
  // Keep the multiplier centered at 1. Volatility should widen the outcome
  // band, not silently improve conversion or worsen churn/CAC on average.
  return Math.exp(sigma * gaussian(rng) - (sigma * sigma) / 2);
}

function percentile(sorted, p) {
  if (!sorted.length) return 0;
  const idx = (sorted.length - 1) * p;
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return sorted[lo];
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo);
}

function summarizeBand(values) {
  const sorted = [...values].sort((a, b) => a - b);
  return {
    p10: roundMoney(percentile(sorted, 0.1)),
    p50: roundMoney(percentile(sorted, 0.5)),
    p90: roundMoney(percentile(sorted, 0.9)),
  };
}

/**
 * Monte Carlo projection — samples conversion, churn, CAC.
 * @param {object} scenario
 * @param {object} pack
 */
export function projectForecastMonteCarlo(scenario, pack) {
  const mc = scenario.monteCarlo || {};
  const runs = Math.min(5000, Math.max(50, Math.floor(Number(mc.runs) || 150)));
  const parsedSeed = Number(mc.seed);
  const seed = Number.isFinite(parsedSeed) ? Math.floor(parsedSeed) : 20260812;
  const horizon = Math.min(
    60,
    Math.max(1, Math.floor(Number(pack.assumptions?.horizonMonths) || 24))
  );
  const cashStart = Math.max(0, Number(pack.assumptions?.startingCash) || 0);
  // Mix growth regimes so P10/P90 span fall-flat → explode — not just noise around Traction.
  const mixRegimes = mc.mixGrowthRegimes !== false;

  const buckets = Array.from({ length: horizon }, () => ({
    mrr: [],
    logos: [],
    bankLinkedLogos: [],
    cash: [],
    profit: [],
    signups: [],
    aiCogsMo: [],
    aiPackRevenue: [],
    variableCogsMo: [],
    plaidCogsMo: [],
    stripeCogsMo: [],
  }));

  const runwayRuns = [];

  for (let r = 0; r < runs; r += 1) {
    const rng = createRng(seed + r * 9973);
    const sampleOverrides = {
      conversionMultiplier: sampleMultiplier(rng, mc.conversionVolPct),
      churnMultiplier: sampleMultiplier(rng, mc.churnVolPct),
      cacMultiplier: pack.channels?.enabled
        ? sampleMultiplier(rng, mc.cacVolPct)
        : 1,
    };

    const runPack = mixRegimes
      ? packWithGrowthRegime(pack, sampleGrowthRegime(rng, mc.growthRegimeMix))
      : pack;

    const path = projectForecastPath(runPack, { rng, sampleOverrides });
    path.months.forEach((month, i) => {
      buckets[i].mrr.push(month.endMrr);
      buckets[i].logos.push(month.endLogos);
      buckets[i].bankLinkedLogos.push(month.bankLinkedLogos);
      buckets[i].cash.push(month.cash);
      buckets[i].profit.push(month.monthlyProfit);
      buckets[i].signups.push(month.signups);
      buckets[i].aiCogsMo.push(month.aiCogsMo);
      buckets[i].aiPackRevenue.push(month.aiPackRevenue);
      buckets[i].variableCogsMo.push(month.variableCogsMo);
      buckets[i].plaidCogsMo.push(month.plaidCogsMo);
      buckets[i].stripeCogsMo.push(month.stripeCogsMo);
    });
    runwayRuns.push(path.runwayMonths);
  }

  const reference = projectForecastPath(pack);

  const months = buckets.map((bucket, i) => {
    const ref = reference.months[i] || {};
    const mrrBand = summarizeBand(bucket.mrr);
    const logosBand = summarizeBand(bucket.logos);
    const logosByPlanP10 = splitLogosByPlan(logosBand.p10, ref.planMix);
    const logosByPlanP50 = splitLogosByPlan(logosBand.p50, ref.planMix);
    const logosByPlanP90 = splitLogosByPlan(logosBand.p90, ref.planMix);
    const bankLinkedBand = summarizeBand(bucket.bankLinkedLogos);
    const cashBand = summarizeBand(bucket.cash);
    const profitBand = summarizeBand(bucket.profit);
    const signupsBand = summarizeBand(bucket.signups);
    const aiCogsBand = summarizeBand(bucket.aiCogsMo);
    const aiPackRevenueBand = summarizeBand(bucket.aiPackRevenue);
    const variableCogsBand = summarizeBand(bucket.variableCogsMo);
    const plaidCogsBand = summarizeBand(bucket.plaidCogsMo);
    const stripeCogsBand = summarizeBand(bucket.stripeCogsMo);

    return {
      ...ref,
      bridgeEndMrr: ref.endMrr,
      endMrr: mrrBand.p50,
      mrrP10: mrrBand.p10,
      mrrP50: mrrBand.p50,
      mrrP90: mrrBand.p90,
      endLogos: logosBand.p50,
      logosP10: logosBand.p10,
      logosP50: logosBand.p50,
      logosP90: logosBand.p90,
      logosByPlan: logosByPlanP50,
      logosByPlanP10,
      logosByPlanP50,
      logosByPlanP90,
      bankLinkedLogos: bankLinkedBand.p50,
      bankLinkedLogosP10: bankLinkedBand.p10,
      bankLinkedLogosP50: bankLinkedBand.p50,
      bankLinkedLogosP90: bankLinkedBand.p90,
      cash: cashBand.p50,
      cashP10: cashBand.p10,
      cashP50: cashBand.p50,
      cashP90: cashBand.p90,
      monthlyProfit: profitBand.p50,
      profitP10: profitBand.p10,
      profitP50: profitBand.p50,
      profitP90: profitBand.p90,
      signups: signupsBand.p50,
      signupsP10: signupsBand.p10,
      signupsP50: signupsBand.p50,
      signupsP90: signupsBand.p90,
      aiCogsMo: aiCogsBand.p50,
      aiCogsMoP10: aiCogsBand.p10,
      aiCogsMoP50: aiCogsBand.p50,
      aiCogsMoP90: aiCogsBand.p90,
      aiPackRevenue: aiPackRevenueBand.p50,
      aiPackRevenueP10: aiPackRevenueBand.p10,
      aiPackRevenueP50: aiPackRevenueBand.p50,
      aiPackRevenueP90: aiPackRevenueBand.p90,
      variableCogsMo: variableCogsBand.p50,
      variableCogsMoP10: variableCogsBand.p10,
      variableCogsMoP50: variableCogsBand.p50,
      variableCogsMoP90: variableCogsBand.p90,
      plaidCogsMo: plaidCogsBand.p50,
      plaidCogsMoP10: plaidCogsBand.p10,
      plaidCogsMoP50: plaidCogsBand.p50,
      plaidCogsMoP90: plaidCogsBand.p90,
      stripeCogsMo: stripeCogsBand.p50,
      stripeCogsMoP10: stripeCogsBand.p10,
      stripeCogsMoP50: stripeCogsBand.p50,
      stripeCogsMoP90: stripeCogsBand.p90,
      mc: true,
    };
  });

  const runwaySorted = [...runwayRuns].sort((a, b) => a - b);
  const monthsToProfitP10 = computeMonthsToProfitFromBand(months, 'p10');
  const monthsToProfitP50 = computeMonthsToProfitFromBand(months, 'p50');
  const monthsToProfitP90 = computeMonthsToProfitFromBand(months, 'p90');
  const cashRecoveryP10 = computeCashRecoveryMonths(
    cashPathForBand(cashStart, months, 'p10')
  );
  const cashRecoveryP50 = computeCashRecoveryMonths(
    cashPathForBand(cashStart, months, 'p50')
  );
  const cashRecoveryP90 = computeCashRecoveryMonths(
    cashPathForBand(cashStart, months, 'p90')
  );

  return {
    months,
    mc: true,
    runs,
    arpu: reference.arpu,
    openingCash: cashStart,
    convRate: reference.convRate,
    runwayMonths: Math.round(percentile(runwaySorted, 0.5)),
    runwayP10: Math.round(percentile(runwaySorted, 0.1)),
    runwayP90: Math.round(percentile(runwaySorted, 0.9)),
    monthsToProfit: monthsToProfitP50,
    monthsToProfitP10,
    monthsToProfitP50,
    monthsToProfitP90,
    cashRecovery: cashRecoveryP50,
    cashRecoveryP10,
    cashRecoveryP50,
    cashRecoveryP90,
  };
}

/**
 * Project all three scenario packs for compare mode.
 * @param {object} scenario
 */
export function projectAllScenarios(scenario) {
  // Compare mode is always deterministic — MC bands live on the active projection only.
  // Re-running MC here doubled cost (500×60) on every assumption edit.
  const keys = ['bear', 'base', 'bull'];
  const deterministic = { ...scenario, monteCarlo: { ...scenario.monteCarlo, enabled: false } };

  const results = {};
  for (const key of keys) {
    results[key] = projectForecast(deterministic, key);
  }

  return results;
}

export function formatMoney(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  const sign = n < 0 ? '-' : '';
  return `${sign}$${Math.abs(n).toFixed(2)}`;
}

export function formatPct(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  return `${(n * 100).toFixed(1)}%`;
}
